import Phaser from "phaser";
import { GAME_WIDTH, STORAGE_KEYS } from "../constants";
import { HAZARD_CLEAR_POINTS } from "../logic/scoring";
import { addCoverImage, addPanel } from "../ui/chrome";
import { ensureControlKeycaps, keycapTextureKey, type KeycapGlyph } from "../ui/keycaps";
import { MenuButton, bindMenuKeys } from "../ui/MenuButton";
import { bodyStyle, prefersReducedMotion, titleStyle } from "../ui/theme";
import { audioManager } from "../systems/AudioManager";
import { assetOrigin } from "../assets/loader";

interface HowToPlayData {
  next?: string;
}

export class HowToPlayScene extends Phaser.Scene {
  private nextScene = "Menu";

  constructor() {
    super("HowToPlay");
  }

  init(data: HowToPlayData): void {
    this.nextScene = data.next ?? "Menu";
  }

  create(): void {
    audioManager.playMusic("menu");
    addCoverImage(this, "menu-background");
    addPanel(this, GAME_WIDTH / 2, 360, 1000, 640).setAlpha(0.97);

    this.add
      .text(GAME_WIDTH / 2, 82, "How to Play", titleStyle(44))
      .setOrigin(0.5)
      .setColor("#2a1a10")
      .setStroke("#000000", 0);

    ensureControlKeycaps(this);

    const keySize = 54;
    const keyGap = 12;
    const keyX = 236;
    const textX = keyX + keySize + keyGap + keySize + 18;
    const rows: Array<{ glyphs: KeycapGlyph[]; text: string }> = [
      { glyphs: ["left", "right"], text: "Change lanes  ·  arrows or A / D" },
      { glyphs: ["up"], text: "Jump  ·  Up, W, or Space" },
      { glyphs: ["down"], text: "Duck  ·  Down or S  ·  press in air to drop" },
      { glyphs: ["esc"], text: "Pause  ·  Esc" },
    ];

    rows.forEach((row, index) => {
      const y = 142 + index * 62;
      row.glyphs.forEach((glyph, glyphIndex) => {
        this.add
          .image(keyX + glyphIndex * (keySize + keyGap), y, keycapTextureKey(glyph))
          .setDisplaySize(keySize, keySize)
          .setOrigin(0, 0.5);
      });
      this.add
        .text(textX, y, row.text, bodyStyle(22))
        .setOrigin(0, 0.5)
        .setWordWrapWidth(500)
        .setAlign("left");
    });

    this.addDemoBear(1000, 236);

    const examples: Array<{ key: string; label: string; animate?: boolean }> = [
      { key: "hazard-log", label: "Jump over" },
      { key: "hazard-low-branch", label: "Duck under" },
      { key: "wolf-run-toward", label: "Jump the wolf", animate: true },
      { key: "food-honey", label: "Snack" },
    ];
    examples.forEach((item, index) => {
      const x = 310 + index * 190;
      const y = 384;
      this.addExampleIcon(x, y, item.key, item.animate);
      this.add.text(x, y + 50, item.label, bodyStyle(16)).setOrigin(0.5).setWordWrapWidth(170);
    });

    this.add
      .text(
        GAME_WIDTH / 2,
        456,
        `Collect food to earn points. Jump or duck hazards to earn ${HAZARD_CLEAR_POINTS} points. Duck as you collect food for a 20% bonus.\n\nThree hits end the run. Reach the cave before the 60-second timer runs out. On a tablet, swipe to move, jump, or duck.`,
        bodyStyle(18),
      )
      .setOrigin(0.5, 0)
      .setLineSpacing(2)
      .setWordWrapWidth(860);

    const fromGame = this.nextScene === "Game";
    const play = new MenuButton(
      this,
      fromGame ? GAME_WIDTH / 2 - 130 : GAME_WIDTH / 2,
      642,
      fromGame ? "Let’s Run!" : "Back",
      () => {
        localStorage.setItem(STORAGE_KEYS.howto, "1");
        this.scene.start(fromGame ? "Game" : "Menu");
      },
      220,
    );
    if (fromGame) {
      const menu = new MenuButton(this, GAME_WIDTH / 2 + 130, 642, "Menu", () => this.scene.start("Menu"), 220, "secondary");
      bindMenuKeys(this, [play, menu]);
    } else {
      bindMenuKeys(this, [play]);
    }
  }

  private addExampleIcon(x: number, y: number, key: string, animate = false): void {
    if (!this.textures.exists(key)) {
      return;
    }
    const sheet = this.textures.get(key).frameTotal > 1;
    const img = sheet ? this.add.sprite(x, y, key, 0) : this.add.image(x, y, key);
    img.setOrigin(0.5, 0.5);
    const scale = Math.min(120 / img.frame.width, 86 / img.frame.height);
    img.setScale(scale);
    if (
      animate &&
      img instanceof Phaser.GameObjects.Sprite &&
      !prefersReducedMotion() &&
      this.anims.exists(key)
    ) {
      img.play(key);
    }
  }

  private addDemoBear(x: number, y: number): void {
    if (!this.textures.exists("bear-jump-rear") || !this.anims.exists("bear-jump-rear")) {
      return;
    }

    const bear = this.add.sprite(x, y, "bear-jump-rear", 0);
    const origin = assetOrigin("bear-jump-rear");
    bear.setOrigin(origin.x, origin.y).setScale(0.5);

    const alive = (): boolean => bear.active && this.sys.settings.status !== Phaser.Scenes.SHUTDOWN;

    const playDemo = (): void => {
      if (!alive()) {
        return;
      }
      bear.play("bear-jump-rear");
      this.time.delayedCall(640, () => {
        if (!alive()) {
          return;
        }
        if (this.anims.exists("bear-duck-rear")) {
          bear.play("bear-duck-rear");
        }
        this.time.delayedCall(540, () => {
          if (!alive()) {
            return;
          }
          if (this.anims.exists("bear-run-rear")) {
            bear.play("bear-run-rear");
          }
          this.time.delayedCall(720, playDemo);
        });
      });
    };

    playDemo();
  }
}
